import { NormalizedNumber, UnixTimestamp } from '@sparkdotfi/common-universal'
import { useConfig } from 'wagmi'

import { AvailableToStakeRow } from '../components/available-to-stake-panel/AvailableToStakePanel'
import { WithdrawalsTableRow } from '../components/withdrawals-table/WithdrawalsTablePanel'
import { finalizeUnstakeDialogConfig } from '../dialogs/finalize-unstake/FinalizeUnstakeDialog'
import { useOpenStakeWithDelegateDialog } from '../dialogs/stake/useOpenStakeWithDelegateDialog'
import { unstakeDialogConfig } from '../dialogs/unstake/UnstakeDialog'
import { ChartDetails, MainPanelData } from '../types'
import { useChartDetails } from './useChartDetails'
import { useSlashableBalanceWatcher } from './useSlashableBalanceWatcher'
import { useSpkStakingData, Withdrawal } from './useSpkStakingData'
import { UseSpkStakingGeneralStatsResult, useSpkStakingGeneralStats } from './useSpkStakingGeneralStats'
import { useGetBlockExplorerAddressLink } from '@/domain/hooks/useGetBlockExplorerAddressLink'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useConnectModal } from '@/domain/hooks/wallet'
import { useSandbox } from '@/domain/sandbox/useSandbox'
import { useOpenDialog } from '@/domain/state/dialogs/hooks'
import { OpenDialogFunction } from '@/domain/state/dialogs/slice'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { season3Active } from '@/features/points/consts'
import { useSpkTokenRepository } from '@/features/spk/common/useSpkTokenRepository'

const GROWING_REWARD_REFRESH_INTERVAL_IN_MS = 50

export interface UseSpkStakingResult {
  chainId: number
  generalStatsResult: UseSpkStakingGeneralStatsResult
  mainPanelData: MainPanelData
  chartDetails: ChartDetails
  withdrawalsTableRows: WithdrawalsTableRow[]
  availableToStakeRow: AvailableToStakeRow
}

export function useSpkStaking(): UseSpkStakingResult {
  const { account } = useConnectedAddress({ throwOnNotConnected: false })
  const { chainId } = usePageChainId()
  const tokenRepository = useSpkTokenRepository({ chainId, account })
  const wagmiConfig = useConfig()
  const { openConnectModal = () => {} } = useConnectModal()
  const { openSandboxModal } = useSandbox()
  const openDialog = useOpenDialog()
  const getBlockExplorerLink = useGetBlockExplorerAddressLink()

  const chartDetails = useChartDetails()
  const generalStatsResult = useSpkStakingGeneralStats()

  const { spkStakingData } = useSpkStakingData({
    chainId,
    account,
    wagmiConfig,
    tokenRepository,
  })

  const openStakeDialogBase = useOpenStakeWithDelegateDialog()
  function openStakeDialog(): void {
    openStakeDialogBase({
      successViewProceedText: 'Back to Staking',
    })
  }

  if (import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging') {
    // biome-ignore lint/correctness/useHookAtTopLevel: <missing explanation>
    useSlashableBalanceWatcher({ slashableBalance: spkStakingData.slashableBalance })
  }

  const spk = tokenRepository.findOneBySymbol(TokenSymbol('SPK'))
  const spkBalance = tokenRepository.findOneBalanceOrZero(TokenSymbol('SPK'))

  const mainPanelData: MainPanelData = (() => {
    if (!account) {
      return {
        type: 'cta',
        props: {
          type: 'disconnected',
          connectWallet: openConnectModal,
          tryInSandbox: openSandboxModal,
          epochDuration: spkStakingData.epochDuration,
        },
      } satisfies MainPanelData
    }

    if (
      spkStakingData.pointsPerSecond.isZero() &&
      spkStakingData.slashableBalance.isZero() &&
      spkStakingData.pointsLastUpdatedAmount.isZero()
    ) {
      return {
        type: 'cta',
        props: {
          type: 'connected',
          stake: openStakeDialog,
          spkBalance,
          epochDuration: spkStakingData.epochDuration,
        },
      } satisfies MainPanelData
    }

    function calculateReward(timestampInMs: number): NormalizedNumber {
      const timestampInSec = timestampInMs / 1000
      const timestamp = Math.max(timestampInSec, spkStakingData.pointsLastUpdatedTimestamp)
      return spkStakingData.pointsLastUpdatedAmount.plus(
        spkStakingData.pointsPerSecond.times(timestamp - spkStakingData.pointsLastUpdatedTimestamp),
      )
    }

    const refreshGrowingRewardIntervalInMs =
      spkStakingData.pointsPerSecond.gt(0) || spkStakingData.amountStaked.gt(0)
        ? GROWING_REWARD_REFRESH_INTERVAL_IN_MS
        : undefined

    const pointsSeason = season3Active ? 3 : 2

    return {
      type: 'active',
      props: {
        stakedAmount: spkStakingData.amountStaked,
        stakingToken: tokenRepository.findOneBySymbol(TokenSymbol('SPK')),
        pointsSeason,
        previousSeasonPoints: spkStakingData.previousSeasonPoints,
        arePointsOutOfSync: spkStakingData.isOutOfSync,
        refreshGrowingRewardIntervalInMs,
        calculateReward,
        openUnstakeDialog: () => openDialog(unstakeDialogConfig, {}),
        openStakeDialog,
      },
    } satisfies MainPanelData
  })()

  const withdrawalsTableRows = formatWithdrawals({
    withdrawals: spkStakingData.withdrawals,
    timestamp: spkStakingData.timestamp,
    tokenRepository,
    openDialog,
  })

  const availableToStakeRow: AvailableToStakeRow = {
    token: spk,
    balance: spkBalance,
    blockExplorerLink: getBlockExplorerLink(spk.address) ?? '/',
    openStakeDialog,
  }

  return {
    chainId,
    generalStatsResult,
    mainPanelData,
    chartDetails,
    withdrawalsTableRows,
    availableToStakeRow,
  }
}

function formatWithdrawals({
  withdrawals,
  timestamp,
  tokenRepository,
  openDialog,
}: {
  withdrawals: Withdrawal[]
  timestamp: UnixTimestamp
  tokenRepository: { findOneBySymbol: (symbol: TokenSymbol) => Token }
  openDialog: OpenDialogFunction
}): WithdrawalsTableRow[] {
  return withdrawals
    .map((withdrawal) => {
      const timeToClaim = getTimeToClaim(UnixTimestamp.fromDate(withdrawal.claimableAt), timestamp)
      const isActionEnabled = timeToClaim === 0

      return {
        token: tokenRepository.findOneBySymbol(TokenSymbol('SPK')),
        amount: withdrawal.amount,
        timeToClaim,
        claimableAt: withdrawal.claimableAt,
        action: () => openDialog(finalizeUnstakeDialogConfig, {}),
        actionName: 'Finalize',
        isActionEnabled,
      }
    })
    .sort((a, b) => a.timeToClaim - b.timeToClaim)
}

function getTimeToClaim(claimableAt: UnixTimestamp, timestamp: UnixTimestamp): number {
  if (claimableAt.lt(timestamp)) {
    return 0
  }
  return Number(claimableAt.since(timestamp).toSeconds())
}
